import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
    Card,
    Typography,
    Button,
    Spin,
    Alert,
    Descriptions,
    message,
    Table,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    AutoComplete,
} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import {LeftOutlined} from "@ant-design/icons";
import {boldLabelStyle, maxW700, NoWrap} from "../../styles/styles";

const {Title} = Typography;
const {Option} = Select;

const playStyleOptions = [
  { label: "Unknown", value: "Unknown" },
  { label: "Flat (low risk)", value: "Flat (low risk)" },
  { label: "Progressive (medium risk)", value: "Progressive (medium risk)" },
  { label: "Tilt (high risk)", value: "Tilt (high risk)" },
];

const CustomerDetailsPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<any>(null);
    const [trips, setTrips] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isEditing, setIsEditing] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCustomer();
        fetchCustomerTrips();
    }, [id]);

    const fetchCustomer = async () => {
        try {
            const res = await api.get(`/daia/customers/${id}/`);
            setCustomer(res.data);
            form.setFieldsValue({
                ...res.data,
                birthday: res.data.birthday ? dayjs(res.data.birthday) : null
            });
        } catch (err) {
            console.error("Failed to fetch customer", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerTrips = async () => {
        try {
            const res = await api.get(`/daia/trips/?customer=${id}`);
            const list = Array.isArray(res.data) ? res.data : res.data.results || [];
            setTrips(list);
        } catch (err) {
            console.error("Failed to fetch trips", err);
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                birthday: values.birthday ? values.birthday.format("YYYY-MM-DD") : null
            };
            await api.patch(`/daia/customers/${id}/`, payload);
            message.success("Customer info updated");
            setIsEditing(false);
            fetchCustomer();
        } catch (err) {
            console.error("Update failed", err);
            message.error("Failed to update customer");
        }
    };

    if (loading) return <Spin size="large"/>;
    if (!customer) return <Alert message="Customer not found" type="error" showIcon/>;

    return (
        <div style={{padding: 24}}>
            <Button icon={<LeftOutlined/>} onClick={() => navigate(-1)} style={{marginBottom: 16}}>
                Back
            </Button>

            <Title level={2} style={{marginBottom: 16}}>
                {customer.first_name} {customer.last_name} (MMID: {customer.mmid})
            </Title>

            <Card
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 12,
                        }}
                    >
                        <Title level={4} style={{margin: 0}}>
                            DAIA Customer Overview
                        </Title>
                        <Button type="primary" onClick={() => setIsEditing(true)}>
                            Edit
                        </Button>
                    </div>
                }
                style={{margin: "16px 0", padding: "16px"}}
            >
                <Descriptions bordered column={1} size="middle" style={maxW700}>
                    <Descriptions.Item label="First Name"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.first_name}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Last Name"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.last_name}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Nationality"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.nationality}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Phone"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.phone || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Birthday"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.birthday || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Preferred Language"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.language || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="VIP Status"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.vip_status || "Regular"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Play Style"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.play_style || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Hotel Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.hotel_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Game Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.game_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Drink Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.drink_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Food Preference"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.food_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Allergies"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.allergies || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item
                        label="Minimum Bet Preference"
                        labelStyle={boldLabelStyle}><NoWrap>{customer.min_bet_preference || "N/A"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Notes"
                                       labelStyle={boldLabelStyle}><NoWrap>{customer.notes || "N/A"}</NoWrap></Descriptions.Item>
                </Descriptions>
            </Card>

            <Modal
                title="Edit Customer Info"
                open={isEditing}
                onCancel={() => setIsEditing(false)}
                onOk={handleSave}
                okText="Save"
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="First Name" name="first_name" rules={[{required: true}]}><Input/></Form.Item>
                    <Form.Item label="Last Name" name="last_name" rules={[{required: true}]}><Input/></Form.Item>
                    <Form.Item label="Nationality" name="nationality"><Input/></Form.Item>
                    <Form.Item label="Phone" name="phone"><Input/></Form.Item>
                    <Form.Item label="Birthday" name="birthday">
                        <DatePicker style={{width: '100%'}} format="YYYY-MM-DD"/>
                    </Form.Item>
                    <Form.Item label="Preferred Language" name="language">
                        <AutoComplete
                            options={["English", "Korean", "Japanese", "Mandarin", "Thai"].map(lang => ({value: lang}))}
                            filterOption={(inputValue, option) =>
                                option!.value.toLowerCase().includes(inputValue.toLowerCase())
                            }
                        >
                            <Input/>
                        </AutoComplete>
                    </Form.Item>
                    <Form.Item label="VIP Status" name="vip_status">
                        <Select>
                            <Option value="Regular">Regular</Option>
                            <Option value="VIP">VIP</Option>
                            <Option value="VVIP">VVIP</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="Play Style" name="play_style">
                        <Select options={playStyleOptions}/>
                    </Form.Item>
                    <Form.Item label="Hotel Preference" name="hotel_preference"><Input/></Form.Item>
                    <Form.Item label="Game Preference" name="game_preference"><Input/></Form.Item>
                    <Form.Item label="Drink Preference" name="drink_preference"><Input/></Form.Item>
                    <Form.Item label="Food Preference" name="food_preference"><Input/></Form.Item>
                    <Form.Item label="Allergies" name="allergies"><Input/></Form.Item>
                    <Form.Item label="Minimum Bet Preference" name="min_bet_preference"><Input/></Form.Item>
                    <Form.Item label="Notes" name="notes"><Input.TextArea rows={4}/></Form.Item>
                </Form>
            </Modal>

            <Card title="Trip History" style={{marginTop: 24}}>
                <Table
                    rowKey="id"
                    dataSource={trips}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination({current: page, pageSize});
                        },
                    }}
                    scroll={{x: "max-content"}}
                    columns={[
                        {title: 'Trip ID', dataIndex: 'id'},
                        {title: 'Check-in', dataIndex: 'checkin_date'},
                        {
                            title: 'Check-out',
                            dataIndex: 'checkout_date',
                            render: (date) => date || 'Ongoing',
                        },
                        {
                            title: 'Buy-in',
                            dataIndex: 'buyin_amount',
                            render: (amount) => `₩${parseFloat(amount).toLocaleString()}`,
                        },
                        {
                            title: 'Win/Loss',
                            dataIndex: 'win_loss',
                            render: (amount) => (
                                <span style={{color: amount >= 0 ? 'green' : 'red'}}>
              ₩{parseFloat(amount).toLocaleString()}
            </span>
                            ),
                        },
                        {
                            title: 'P&L',
                            dataIndex: 'pnl',
                            render: (amount) => (
                                <span style={{color: amount >= 0 ? 'green' : 'red'}}>
              ₩{parseFloat(amount).toLocaleString()}
            </span>
                            ),
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

export default CustomerDetailsPage;
